import { locateProxy } from '#~/agent/utils.js';
import { toIdFromHex } from '#~/api/utils.js';
import { isElectronApp } from '#~/electron/utils.js';
import { generateLocalTokenAndGetIPs } from '#~/livekit/livekit.js';
import { logger } from '#~/logger/index.js';
import { DefaultBitrate } from '#~/video/constants.js';
import { locateDeviceByPersistentID } from './discovery.js';
import type { Device } from '@studio/models/device';
import {
  StatusDeviceConnectionStatus,
  StatusStreamState
} from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { T_Maybe } from '@tryiris-ai/js-types';
import {
  LocalTrackPublication,
  Room,
  RoomEvent,
  type AudioCaptureOptions,
  type VideoCaptureOptions,
  ConnectionQuality,
  type TrackPublishOptions
} from 'livekit-client';

export const pipelines = new Map<string, MediaDevicePipeline>();

const qualityToScore: Record<ConnectionQuality, number> = {
  [ConnectionQuality.Excellent]: 1,
  [ConnectionQuality.Good]: 0.75,
  [ConnectionQuality.Poor]: 0.25,
  [ConnectionQuality.Lost]: 0.0,
  [ConnectionQuality.Unknown]: 0.0
};

//let mode: 'cloud' | 'local' = 'cloud';

export let localDeviceIdURXMapping: Map<string, string> = new Map();

export const parseURX = (urx: string) => {
  const urxParts = urx.split(',');

  const uri = urxParts.find((part) => part.startsWith('browsermedia://'));

  if (!uri) {
    return {};
  }

  // parse query from urx
  const query = uri.split('?')[1];

  if (!query) {
    return {};
  }

  const params = new URLSearchParams(query);

  const path = params.get('path') ?? '';

  const persistentID = path;

  const token = params.get('livekit.token');
  const endpoint = params.get('livekit.endpoint');

  const widthStr = params.get('width') ?? undefined;
  const heightStr = params.get('height') ?? undefined;
  const framerateStr = params.get('framerate') ?? undefined;

  const bitrateStr = params.get('bitrate') ?? undefined;

  const width = widthStr ? parseInt(widthStr) : undefined;
  const height = heightStr ? parseInt(heightStr) : undefined;
  const framerate = framerateStr ? parseInt(framerateStr) : undefined;
  const bitrate = bitrateStr ? parseInt(bitrateStr) : undefined;

  return { persistentID, token, width, height, framerate, bitrate, endpoint };
};

const lockMap = new Map<string, boolean>();

export class MediaDevicePipeline {
  private mediaDeviceID: string | undefined;
  private localTrackPublication?: LocalTrackPublication | undefined;
  private statusTickInterval?: NodeJS.Timeout;

  private qualityScore: number = 0;

  private room: T_Maybe<Room>;

  private token: string;
  private endpoint: string;

  private resolution: {
    width?: number | undefined;
    height?: number | undefined;
    fps?: number | undefined;
    bitrate?: number | undefined;
  };

  constructor(
    private readonly agentID: string,
    private readonly deviceID: string,
    private readonly kind: 'audio' | 'video',
    private readonly persistentID: string,
    private readonly urx: string
  ) {
    const { token, width, height, framerate, bitrate, endpoint } = parseURX(urx);
    this.token = token!;
    this.endpoint = endpoint!;
    this.resolution = {
      width,
      height,
      fps: framerate,
      bitrate
    };
  }

  starting: boolean = false;
  stopping: boolean = false;
  started: boolean = false;

  async start(): Promise<void> {
    if (!isElectronApp('worker')) {
      return;
    }

    if (this.started || this.starting) {
      return;
    }

    if (lockMap.get(this.deviceID)) {
      return;
    }

    lockMap.set(this.deviceID, true);

    this.starting = true;

    try {
      await this.execStart();
    } catch (error) {
      // ENG-3716 / per rule settle-every-terminal-branch (ai.knowledge/global):
      // a failed start used to leave `starting` and the per-device lock set
      // forever, so no retry could ever run and the source stayed
      // "Connecting..." with no escape hatch. Settle both, then rethrow so the
      // caller can key its failure UI state.
      try {
        // A publish failure can occur after the room and pipeline are registered.
        // Disconnect that partial session before allowing a retry to own the ID.
        await this.room?.disconnect();
      } catch (cleanupError) {
        logger.warning(
          `Failed to disconnect partial media pipeline ${this.deviceID}: ${cleanupError}`
        );
      } finally {
        if (this.statusTickInterval !== undefined) clearInterval(this.statusTickInterval);
        this.localTrackPublication = undefined;
        this.starting = false;
        this.started = false;
        this.stopping = false;
        if (pipelines.get(this.deviceID) === this) pipelines.delete(this.deviceID);
        lockMap.delete(this.deviceID);
        localDeviceIdURXMapping.delete(this.deviceID);
      }
      throw error;
    }
  }

  async execStart(): Promise<void> {
    localDeviceIdURXMapping.set(this.deviceID, this.urx);

    // ENG-3716: validate the (possibly snapshot-restored) device id against the
    // live media-device discovery BEFORE committing to a room connection — a
    // device id absent from discovery must fail fast instead of connecting a
    // room it can never publish into.
    const mediaDeviceId = await this.resolveMediaDeviceID();
    if (!mediaDeviceId) {
      throw new Error('No media device ID found');
    }

    const startingMode = String('local') as 'cloud' | 'local';

    this.room = await createRoomWithToken(this.token, this.endpoint, startingMode, this.agentID);

    await new Promise((resolve) => {
      setTimeout(resolve, 1000);
    });

    pipelines.set(this.deviceID, this);

    if (this.kind === 'audio') {
      const audioConstraints: AudioCaptureOptions = {
        deviceId: mediaDeviceId
      };
      this.localTrackPublication = await this.room.localParticipant.setMicrophoneEnabled(
        true,
        audioConstraints
      );
    } else {
      const videoConstraints: VideoCaptureOptions = {
        resolution: {
          width: this.resolution.width ?? 1920,
          height: this.resolution.height ?? 1080,
          frameRate: this.resolution.fps ?? 30
        },
        deviceId: mediaDeviceId
      };

      const encodingConstraints: TrackPublishOptions = {
        videoEncoding: {
          maxBitrate: this.resolution.bitrate ?? DefaultBitrate,
          maxFramerate: (this.resolution.fps ?? 30) + 10
        },
        videoCodec: 'vp9',
        scalabilityMode: 'L1T3',
        dtx: false,
        red: false,
        simulcast: false,
        videoSimulcastLayers: [],
        // @ts-ignore
        ...((window.ENCODING_OVERRIDES as TrackPublishOptions) ?? {})
      };

      this.localTrackPublication = await this.room.localParticipant.setCameraEnabled(
        true,
        videoConstraints,
        encodingConstraints
      );
    }

    this.resetPipelineMetrics();

    if (this.statusTickInterval !== undefined) {
      clearInterval(this.statusTickInterval);
    }

    this.statusTickInterval = setInterval(() => {
      this.pingStatus();
    }, 1000);

    this.room.on(RoomEvent.ConnectionQualityChanged, (score: ConnectionQuality) => {
      this.qualityScore = qualityToScore[score] ?? 0;
    });

    this.starting = false;
    this.started = true;
  }

  private lastBytesReceived = 0;
  private lastFramesDecoded = 0;
  private lastTimestamp = Date.now();

  private extractVideoMetrics(stats: RTCStatsReport): {
    fps: number;
    resolution: { width: number; height: number };
    bitrate: number;
    codec: string | undefined;
  } {
    let bytesReceived = 0;
    let framesDecoded = 0;
    let width = 0;
    let height = 0;

    let codecStats: any;

    stats.forEach((report) => {
      if (report.type === 'outbound-rtp') {
        if (report.bytesSent !== undefined) {
          bytesReceived = Math.max(report.bytesSent, bytesReceived);
        }

        if (report.framesSent !== undefined) {
          framesDecoded = Math.max(report.framesSent, framesDecoded);
        }

        if (report.frameWidth !== undefined) {
          width = Math.max(report.frameWidth, width);
        }

        if (report.frameHeight !== undefined) {
          height = Math.max(report.frameHeight, height);
        }
      }

      const codecResults = report.codecId ? stats.get(report.codecId) : undefined;

      if (codecResults) {
        codecStats = codecResults;
      }
    });

    // Only the audio branch in pingStatus emits this codec to state sync (the
    // video branch intentionally omits it — see the comment there). audio.status.codec
    // feeds the "Sending" codec cell in the monitoring summaries (summary.tsx,
    // summary-lite.tsx, wiring.ts), which render it raw and expect the bare subtype
    // ("opus"). Emitting the full MIME ("audio/opus") would display "audio/opus"
    // there, so strip to the subtype. codec-warning.ts / backend egress validation
    // match full MIME but only on the video codec, which this pipeline does not emit. (ENG-3463)
    const codec = codecStats?.mimeType?.split('/')[1] ?? undefined;

    const currentTimestamp = Date.now();
    const timeElapsed = (currentTimestamp - this.lastTimestamp) / 1000;
    const bpsDiff = bytesReceived - this.lastBytesReceived;
    const framerateDiff = framesDecoded - this.lastFramesDecoded;

    const bitrate = (bpsDiff / timeElapsed) * 8;
    const fps = framerateDiff / timeElapsed;

    this.lastBytesReceived = bytesReceived;
    this.lastFramesDecoded = framesDecoded;
    this.lastTimestamp = currentTimestamp;

    return {
      fps,
      resolution: { width, height },
      bitrate,
      codec
    };
  }

  private resetPipelineMetrics = (): void => {
    this.lastBytesReceived = 0;
    this.lastFramesDecoded = 0;
    this.lastTimestamp = Date.now();
  };

  private async pingStatus(): Promise<void> {
    const stats = await this.localTrackPublication?.track?.getRTCStatsReport();

    if (!stats) {
      return;
    }

    const metrics = this.extractVideoMetrics(stats);

    const proxy = locateProxy(this.deviceID as Device['id']);

    if (!proxy) {
      return;
    }

    proxy.mutate(
      {
        status: {
          device: {
            [this.deviceID]:
              this.kind === 'audio'
                ? {
                    audio: {
                      connectionStatus: {
                        status: StatusDeviceConnectionStatus.CONNECTION_STATUS_CONNECTED
                      },

                      status: {
                        bps: metrics.bitrate,
                        // @ts-ignore codec field exists in agent.proto but @tryiris-ai/common.protojs has not been bumped yet (ENG-3463)
                        codec: metrics.codec,
                        state: StatusStreamState.STREAM_STATE_PLAYING
                      }
                    }
                  }
                : {
                    video: {
                      connectionStatus: {
                        status: StatusDeviceConnectionStatus.CONNECTION_STATUS_CONNECTED
                      },

                      status: {
                        framerate: metrics.fps,
                        width: metrics.resolution.width,
                        height: metrics.resolution.height,
                        bps: metrics.bitrate,
                        // Browser media video codec is intentionally NOT emitted to state sync.
                        // WebRTC negotiates VP9 by default (line 181), but LiveKit egress
                        // transcodes VP9 → H264 for Recording/RTMP downstream. Emitting
                        // "video/VP9" here would trigger ValidateEgressCodec to reject
                        // browser sources for Recording/RTMP even though they work. See
                        // TestIntegration_EmptyCodecSkipsAllFeatures in codec_support_test.go
                        // — the design intent is that browser sources skip codec validation
                        // and the backend's Layer 1 safety net catches anything that truly
                        // can't transcode at start time. (ENG-3463)
                        uri: 'browsermedia://',
                        network: this.qualityScore,
                        state: StatusStreamState.STREAM_STATE_PLAYING
                      }
                    }
                  }
          }
        }
      },
      {},
      false
    );
  }

  async stopPipeline(): Promise<void> {
    if (this.localTrackPublication) {
      this.room?.localParticipant.setCameraEnabled(false);
      this.room?.localParticipant.setMicrophoneEnabled(false);
      this.localTrackPublication = undefined;
    }
    if (this.statusTickInterval) {
      clearInterval(this.statusTickInterval);
    }
    this.room?.disconnect();

    this.started = false;
    this.stopping = false;
    this.starting = false;
  }

  async stop(): Promise<void> {
    if (this.starting) {
      this.stopping = true;
      return;
    }

    if (!this.started) {
      return;
    }

    await this.stopPipeline();

    pipelines.delete(this.deviceID);
    localDeviceIdURXMapping.delete(this.deviceID);

    lockMap.delete(this.deviceID);
  }

  async resolveMediaDeviceID(): Promise<string | undefined> {
    const device = await locateDeviceByPersistentID(this.kind, this.persistentID);
    this.mediaDeviceID = device?.id;
    return this.mediaDeviceID;
  }
}

const decodeBase64Url = (input: string): string => {
  const base64 = input.replace(/-/g, '+').replace(/_/g, '/');
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  return atob(base64 + padding);
};

const extractMetadata = (token: string): { room: string; sub: string } => {
  try {
    const [, payloadBase64] = token.split('.');

    if (!payloadBase64) {
      throw new Error('Invalid token format');
    }

    const payloadJson = decodeBase64Url(payloadBase64);
    const payload = JSON.parse(payloadJson);

    const room = payload.video?.room;
    const sub = payload.sub;

    if (!room || !sub) {
      throw new Error('Required fields not found in token');
    }

    return { room, sub };
  } catch (error) {
    console.error('Error decoding token:', error);
    throw new Error('Invalid token');
  }
};

const startingRoomMap = new Map<string, Room>();

export const createRoomWithToken = async (
  token: string,
  endpoint: string,
  mode: 'cloud' | 'local',
  agentId: string
): Promise<Room> => {
  const meta = extractMetadata(token);

  if (startingRoomMap.get(meta.sub)) {
    return startingRoomMap.get(meta.sub)!;
  }

  const room = new Room({
    adaptiveStream: true,
    dynacast: true
  });

  startingRoomMap.set(meta.sub, room);

  try {
    if (mode === 'local') {
      // Connect to LiveKit
      //const irisSocket = createIrisSocket(`direct.authenticated.${agentId}.signal.tunnel`, agentId);

      const { token: nextToken, uris } = await generateLocalTokenAndGetIPs(
        toIdFromHex(agentId)!,
        meta.sub,
        meta.room
      );

      const addr = uris[0];

      await room.connect(addr!, nextToken);
    } else {
      await room.connect(endpoint, token);
    }
  } catch (error) {
    // A failed connection has no pipeline owner yet; release its room here.
    try {
      await room.disconnect();
    } catch (cleanupError) {
      logger.warning(`Failed to disconnect unstarted media room: ${cleanupError}`);
    }
    throw error;
  } finally {
    // Per rule: settle-every-terminal-branch (ai.knowledge/global). Failed
    // connections must leave the in-progress cache so the next retry connects.
    startingRoomMap.delete(meta.sub);
  }

  return room;
};

export const tearDownPipelineIfNecessary = async (deviceId: string): Promise<void> => {
  const pipeline = pipelines.get(deviceId);
  if (!pipeline) {
    return;
  }

  console.trace('Tearing down pipeline:', deviceId);

  await pipeline.stop();
  pipelines.delete(deviceId);
};

export const setConnectionMode = async (_nextMode: 'cloud' | 'local'): Promise<void> => {
  // not used we always are using local mode
  return;
  /*
  if (nextMode === mode) {
    return;
  }

  if(mode === undefined) {
    mode = nextMode;
    return;
  }

  mode = nextMode;

  const promises = Array.from(pipelines.values()).map(async (pipeline) => {
    
    if(!pipeline.started && !pipeline.starting) {
      return
    }

    try {
      await pipeline.stopPipeline();
    } catch (error) {
      console.error('Error stopping pipeline:', error);
    }

    if(nextMode !== 'local') {
      return
    }

    try {
      await pipeline.start();
    } catch (error) {
      console.error('Error starting pipeline:', error);
    }
  });

  await Promise.all(promises);
  */
};

export const resetPipelines = async (): Promise<void> => {
  const promises = Array.from(pipelines.values()).map(async (pipeline) => {
    if (!pipeline.started && !pipeline.starting) {
      return;
    }

    try {
      await pipeline.stop();
    } catch (error) {
      console.error('Error stopping pipeline:', error);
    }
  });

  await Promise.all(promises);

  localDeviceIdURXMapping.clear();
  pipelines.clear();

  console.log('Pipelines reset');
};
